<template>
    <b-container fluid class="d-flex flex-column w-100 h-100 p-0 m-0" :style="generateVariantStyle(Variant)">
        <b-row class="h-100" no-gutters v-if="isUserSupervisor || $store.getters['CheckAction']('Read', undefined, Action)">
            <b-col cols="3" class="p-0 border d-flex flex-column" :class="isNewProfil || (!isSaveDisabled && (profil.Id && profil.Name)) ? 'pe-none opacity-50' : ''">
                <div class="my-3 px-3 flex-shrink-0">
                    <!-- Header Profil -->
                    <b-row no-gutters class="d-flex flex-row justify-content-between">
                        <h4 class="font-weight-semibold">{{ $t('Profile') }}</h4>
                        <!-- Bouton d'ajout d'un profil -->
                        <b-button v-b-tooltip.hover
                            @click="addProfil()"
                            :title="$t('Add')"
                            :variant="Variant"
                            :disabled="!isUserSupervisor && !$store.getters['CheckAction']('Add', undefined, Action)"
                            class="btn-circle ml-1 border-0 d-flex align-items-center justify-content-center"
                        >
                            <b-icon-plus/>
                        </b-button>
                    </b-row>
                    <b-row no-gutters class="d-flex flex-row justify-content-start align-items-center mt-3">
                        <!-- Zone de recherche d'un profil -->
                        <b-col cols="12">
                            <b-input-group>
                                <template #prepend>
                                    <b-button :variant="Variant" @mouseover="hovered = true" @mouseleave="hovered = false">
                                        <b-icon font-scale="1.3" class="text-white" rounded pill icon="search"/>
                                    </b-button>
                                </template>
                                <b-form-input :placeholder="$t('Search')" debounce="500" v-model="search"/>
                            </b-input-group>
                        </b-col>
                    </b-row>
                </div>
                <b-col class="flex-grow-1 min-vh-0 overflow-auto m-0 p-0 border-top">
                    <!-- Liste des profils -->
                    <b-list-group flush class="m-0">
                        <b-list-group-item v-for="profilFilter in filteredProfils" :key="profilFilter.Id" class="text-center" button @click="loadProfilValues(profilFilter)"
                            :class="{
                                'bg-quartary-light font-weight-semibold': profilFilter.Id === (profil && profil.Id),
                                'bg-light': profilFilter.Id === (profil && profil.Id),
                                'border-bottom': profilFilter.Id == profils[profils.length - 1].Id,
                            }"
                        >
                            <b-row class="d-flex align-items-center justify-content-between no-gutters">
                                <!-- Nom du profil -->
                                <h6 class="my-2">{{ profilFilter.Name }}</h6>
                                <!-- Bouton de suppresion d'un profil -->
                                <b-button v-b-tooltip.hover
                                    @click="deleteProfil(profilFilter)"
                                    :title="$t('Delete')"
                                    variant="outline-dark"
                                    class="btn-circle-small ml-1 border-0 bg-gray-light d-flex align-items-center justify-content-center"
                                    :disabled="!isUserSupervisor && !$store.getters['CheckAction']('Delete', undefined, Action)"
                                >
                                    <b-icon icon="x" font-scale="0.9" variant="danger"/>
                                </b-button>
                            </b-row>
                        </b-list-group-item>
                    </b-list-group>
                </b-col>
            </b-col>
            <b-col cols="9" class="d-flex flex-column h-100">
                <Welcome :DisplayBrand="false" :Loading="false" :Variant="Variant" v-if="profils.length == 0 || (!isNewProfil && !profil.Id && !profil.Name)">
                    <template v-slot:MiscContent>
                        <h4 class="text-center">{{$t('PleaseSelectProfil')}}</h4> <!-- Veuillez sélectionner une fonction -->
                    </template>
                </Welcome>
                <b-row align-v="center" class="border-left-0 flex-shrink-0 py-3 ml-3" no-gutters v-if="!(profils.length == 0 || (!isNewProfil && !profil.Id && !profil.Name))">
                    <!-- Zone de modification du nom d'un profil -->
                    <b-col cols="6">
                        <b-form-input
                            v-model="profil.Name"
                            class="border-1 shadow-none bg-transparent w-100 fs-5"
                            placeholder="Entrer un nom"
                        />
                        <p class="mt-2 mb-0 small text-muted">{{ $t('Description') }}</p>
                    </b-col>
                    <!-- Bouton pour la modification/ajout d'un profil -->
                    <b-col cols="6" class="d-flex justify-content-end pr-3">
                        <span v-b-tooltip.hover="$t('Cancel')">
                            <b-button 
                                    class="font-weight-semibold mr-3" 
                                    @click="cancelModification" 
                                    :variant="'outline-' + Variant" 
                                    pill
                                    :disabled="isSaveDisabled && !isNewProfil">
                                {{$t('Cancel')}}
                            </b-button>
                        </span>
                        <b-avatar
                            :disabled="(!isUserSupervisor && !$store.getters['CheckAction']('Write', undefined, Action)) || isSaveDisabled"
                            v-b-tooltip.hover :title="$t('Save')"
                            button rounded="pill" @click="saveProfil" 
                            class="w-auto h-auto px-3 font-weight-semibold" :variant="Variant"
                            badge-top badge-variant="info"
                        >
                            <template #badge v-if="!isSaveDisabled">
                                <!-- WarnUpdatedFields : Des champs ont été modifiés -->
                                <b-icon-exclamation
                                    v-b-tooltip.noninteractive.hover :title="$t('WarnUpdatedFields')"
                                />
                            </template>
                            Enregistrer
                        </b-avatar>
                    </b-col>
                </b-row>
                <b-row class="flex-grow-1 min-vh-0 overflow-auto bg-gray-light px-0" no-gutters v-if="!(profils.length == 0 || (!isNewProfil && !profil.Id && !profil.Name))">
                    <b-col class="h-100 px-3 pt-1 border border-right-0 border-bottom-0 border-left-0">
                        <b-list-group flush>
                            <!-- Liste des parents des droits -->
                            <b-list-group-item v-for="HeaderRight in groupedRights" :key="HeaderRight.Id" button v-b-toggle="'group-' + HeaderRight.Id"
                                class="bg-white rounded my-2 p-3">
                                <b-icon :icon="HeaderRight.IsOpen ? 'chevron-down' : 'chevron-right'"/>
                                <span class="ml-4 font-weight-semibold">{{ HeaderRight.Nom }}</span>
                                <b-badge :class="'text-' + getBadgeVariant(HeaderRight.Nom) + '-dark bg-' + getBadgeVariant(HeaderRight.Nom) + '-light'" pill class="p-2 text-center ml-4 font-weight-normal">
									{{ getBadgeText(HeaderRight.Nom) }}
								</b-badge>
                                <b-collapse :id="'group-' + HeaderRight.Id" class="mt-3"
                                    @show="HeaderRight.IsOpen = true"
                                    @hide="HeaderRight.IsOpen = false">
                                    <!-- Liste des enfants de chaque parents -->
                                    <b-list-group flush class="mx-2 rounded">
                                        <b-list-group-item class="bg-gray-light font-weight-semibold">
                                            <b-row class="d-flex align-items-center text-center">
                                                <b-col cols="4" class="d-flex justify-content-center align-items-center">{{ $t('Function') }}</b-col>
                                                <b-col cols="2" class="d-flex justify-content-center align-items-center">{{ $t('Read') }}</b-col>
                                                <b-col cols="2" class="d-flex justify-content-center align-items-center">{{ $t('Write') }}</b-col>
                                                <b-col cols="2" class="d-flex justify-content-center align-items-center">{{ $t('Add') }}</b-col>
                                                <b-col cols="2" class="d-flex justify-content-center align-items-center">{{ $t('Delete') }}</b-col>
                                            </b-row>
                                        </b-list-group-item>
                                        <b-list-group-item v-for="(right, index) in HeaderRight.Rights" :key="index">
                                            <b-row class="d-flex align-items-center">
                                                <b-col cols="4">{{ right.Function }}</b-col>
                                                <b-col cols="2" class="d-flex justify-content-center align-items-center"><b-form-checkbox class="ml-2" v-model="right.Read" @click.native.stop @change="checkSelection($event, right.FunctionName, 'Read')"/></b-col>
                                                <b-col cols="2" class="d-flex justify-content-center align-items-center"><b-form-checkbox class="ml-2" v-model="right.Write" @click.native.stop @change="checkSelection($event, right.FunctionName, 'Write')" /></b-col>
                                                <b-col cols="2" class="d-flex justify-content-center align-items-center"><b-form-checkbox class="ml-2" v-model="right.Add" @click.native.stop @change="checkSelection($event, right.FunctionName, 'Add')" /></b-col>
                                                <b-col cols="2" class="d-flex justify-content-center align-items-center"><b-form-checkbox class="ml-3" v-model="right.Delete" @click.native.stop @change="checkSelection($event, right.FunctionName, 'Delete')"/></b-col>
                                            </b-row>
                                        </b-list-group-item>
                                    </b-list-group>
                                </b-collapse>
                            </b-list-group-item>
                        </b-list-group>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
    </b-container>
</template>

<script lang="ts">
    import { Component, Vue, PropSync, Prop } from "vue-property-decorator";
    import axios, { AxiosResponse } from 'axios';
    import Dictionary from "@/types/Dictionary";
    import { API_URL } from '@/App.settings';
    import { Getter } from 'vuex-class';

    /** i18n pour la traduction */
    import locales from './locales/Profil.json';

    import { ThemeUtils } from "@/components/commons/Utils";
    import Welcome from '@/components/commons/Welcome.vue';

    /**
     * Composant qui affiche les demandes utilisateurs pour les services après ventes
    */
	@Component({
        components: {
            Welcome
		},
        i18n:{messages: locales}
	})
    export default class Profil extends ThemeUtils {

        /** Variant de couleur à appliquer */
        @Prop({default: () => 'primary'})
        public Variant: string;

        /** Droit disponible pour l'utilisateur en cours */
        @Prop()
        public Action!: string;

        @Getter('SessionStore/GetUserIsSupervisor')
        private isUserSupervisor : boolean;

        private isNewProfil: boolean = false;

        /** Attribut pour récupérer l'ensemble des profils */
        private profils: ProfilDto[] = [];

        /** Attribut pour stocker l'ensemble des groupes possibles */
        private groupedRights: Group[] = [];

        /** Attribut pour récupérer le profil sélectionnée par l'utilisateur */
        private profil: ProfilDto = {
            Id: '',
            Name: '',
            Rights: []
        };

        /** Attribut pour garder en mémoire le profil avant modification si l'utilisateur clique ensuite sur annuler */
        private profilBackup: ProfilDto = {
            Id: '',
            Name: '',
            Rights: []
        };

        /** Attribut pour récupérer la liste des droits disponibles */
        private rights: Array<Right> = [];

        /** Attribut pour garder en mémoire la liste des droits du profil avant modification */
        private rightsBackup: Array<Right> = [];

        /** Attribut pour lister les actions possibles */
        private actions: Array<string> = ['Read', 'Write', 'Add', 'Delete'];
        
        /** Filtre de recherche */
        private search: string = "";

        /** Attribut pour gérer la couleur de l'icon de recherche */
        private hovered: boolean = false;

        /** Liste des couleurs pour chaque type de badge */
        private readonly badgeVariants: Record<string, string> = {
            [BadgeTypeEnum.None]: 'gray',
            [BadgeTypeEnum.Personalized]: 'warning',
            [BadgeTypeEnum.Full]: 'success',
            [BadgeTypeEnum.Read]: 'secondary',
            [BadgeTypeEnum.Write]: 'quartary',
            [BadgeTypeEnum.Add]: 'tertiary',
        };

        /** Chargement du tableau - Affichage de l'icône de chargement */
        @PropSync('Loading', {default: () => false})
        public loading!: boolean;

        /** Méthode pour récupérer la liste des profiles */
        private async getProfils() {
            this.$store.dispatch("ShowLoadingMask");
            const args: Dictionary<any> = {};
            args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

            await axios.post(API_URL + "Profil/GetProfils", args).then((response) => {
                if (response && response.data) {
                    console.debug("Profil - getProfils - response.data", response.data);

                    this.profils = response.data;
                }
            }).catch(err => {
                console.error(err);
                this.$bvToast.toast(this.$t('AnErrorOccured').toString(), {
                        title: this.$t('Error').toString(),
                        autoHideDelay: 10000,
                        variant: "danger",
                        solid: true,
                        appendToast: true,
                        toaster: "b-toaster-top-center"
                    });
            }).finally(() => {
                this.$store.dispatch("HideLoadingMask", {});
            });
        }

        /** Méthode pour récupérer la liste de toutes les fonctions assignables à un profil */
        private async getFunctions() {
            const args: Dictionary<any> = {};
            args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

            await axios.post(API_URL + "Profil/GetFunctions", args).then((response) => {
                if (response && response.data) {
                    console.debug("Profil - getFunctions - response.data", response.data);

                    this.rights = response.data;
                }
            }).catch(err => {
                console.error(err);
                this.$bvToast.toast(this.$t('AnErrorOccured').toString(), {
                        title: this.$t('Error').toString(),
                        autoHideDelay: 10000,
                        variant: "danger",
                        solid: true,
                        appendToast: true,
                        toaster: "b-toaster-top-center"
                    });
            })
        }

        /** Méthode pour charger les droits correspondant à un seul profil */
        private loadProfilValues(profil: ProfilDto) {
            this.profil = profil;
            this.profilBackup = { ...profil };
            this.buildGroupedRights();
            this.rightsBackup = JSON.parse(JSON.stringify(this.rights));

            console.debug('Profil - loadProfilValues - ', this.profil);
        }

        /** Méthode qui sert à enlever les droits d'un profil sélectionné pour ajouter un nouveau profil */
        private addProfil()
        {
            // Si l'utilisateur n'a pas les droit en ajout, on sort de la fonction
            if (!this.isUserSupervisor && !this.$store.getters['CheckAction']('Add', undefined, this.Action)) {
                console.warn("addProfil() : pas les droits pour ajouter un profil");
                return;
            }

            if(this.profils.filter(p => p.Id == '').length > 0)
            {
                this.$bvToast.toast(this.$t('ProfilSaveRequired').toString(), {
                        title: this.$t('Warning').toString(),
                        autoHideDelay: 10000,
                        variant: "warning",
                        solid: true,
                        appendToast: true,
                        toaster: "b-toaster-top-center"
                    });
                return;
            }

            this.profil = {
                Id: '',
                Name: this.$t('NewProfile').toString(),
                Rights: []
            };
            this.profilBackup = this.profil;
            this.isNewProfil = true;
            this.profils.push(this.profil);
            this.loadProfilValues(this.profil);
        }

        /** Méthode pour supprimer un profil */
        private async deleteProfil(profil: ProfilDto) {
            // Si l'utilisateur n'a pas les droits pour supprimer un profil, on sort de la fonction
            if (!this.isUserSupervisor && !this.$store.getters['CheckAction']('Delete', undefined, this.Action)) {
                console.warn("deleteProfil() : pas les droits pour supprimer un profil");
                return;
            }

            let message = [];
            /** forge du message de validation sur le modèle de celui de Sandrine */
            message.push(this.$t('DeleteProfil').toString());
            message.push(this.$createElement('div', [this.$createElement('b-icon', {props: {icon: 'arrow-right'}}), "  " + profil.Name]));
            message.push(this.$createElement('br'));
            message.push(this.$createElement('div', [
                this.$createElement('b-icon', {props: {icon: 'exclamation-triangle-fill', variant: "warning"}}),
                "  " + this.$t('MsgConfirmInfoDelete').toString()
            ]));

            this.$bvModal.msgBoxConfirm([this.$createElement('p', message)], {
                title: this.$t('DeleteProfil').toString(),
                okVariant: 'danger',
                cancelVariant: 'light',
                okTitle: this.$t('Delete').toString(),
                cancelTitle: this.$t('No').toString(),
                hideHeaderClose: false,
                centered: true
            }).then(async value => {
                // Si l'utilisateur à annulé la suppression, on sort
                if(!value) {
                    return;
                }

                this.$store.dispatch("ShowLoadingMask");
                const args: Dictionary<any> = {};
                args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
                args["Id"] = profil.Id;

                await axios.post(API_URL + "Profil/DeleteProfil", args).then((response) => {
                }).catch(err => {
                    console.debug(err);
                    this.$bvToast.toast(err.response.data, {
                        title: this.$t('Error').toString(),
                        autoHideDelay: 10000,
                        variant: "danger",
                        solid: true,
                        appendToast: true,
                        toaster: "b-toaster-top-center"
                    });
                })
            }).finally(() => {
                this.getProfils();

                this.profil = {
                    Id: '',
                    Name: '',
                    Rights: []
                };

                this.profilBackup = {
                    Id: '',
                    Name: '',
                    Rights: []
                };

                this.$store.dispatch("HideLoadingMask", {});
            });
        }

        /** Méthode pour enregistrer un nouveau profil */
        private async saveProfil() {
            if (!this.isUserSupervisor && !this.$store.getters['CheckAction']('Write', undefined, this.Action)) {
                console.warn("saveProfil() : pas les droits pour save un profil");
                return;
            }

            const args: Dictionary<any> = {};
            args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
            args["Id"] = this.profil.Id;
            args["Name"] = this.profil.Name;
            args["Rights"] = this.rights;

            if(this.rights.filter(r => r.Add || r.Write || r.Read || r.Delete).length == 0)
            {
                this.$bvToast.toast(this.$t('NoRightsAssigned').toString(), {
                    title: this.$t('Warning').toString(),
                    autoHideDelay: 10000,
                    variant: "warning",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
                return;
            }

            if(this.profil.Name == '')
            {
                this.$bvToast.toast(this.$t('EmptyName').toString(), {
                    title: this.$t('Warning').toString(),
                    autoHideDelay: 10000,
                    variant: "warning",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
                return;
            }

            await this.$store.dispatch("ShowLoadingMask");

            try {
                const response: AxiosResponse<any,any> = await axios.post(API_URL + "Profil/SaveProfil", args, {});
            
                console.debug("Profil - SaveProfil - response.data", response.data);
            } catch (error) {
                console.error(error);
                this.$bvToast.toast(this.$t('AnErrorOccured').toString(), {
                        title: this.$t('Error').toString(),
                        autoHideDelay: 10000,
                        variant: "danger",
                        solid: true,
                        appendToast: true,
                        toaster: "b-toaster-top-center"
                    });
            } finally {
                await this.getProfils();
                this.isNewProfil = false;
                this.profilBackup = { ...this.profil };
                this.rightsBackup = JSON.parse(JSON.stringify(this.rights));

                let profil: ProfilDto = null;

                // S'il s'agit d'une modification de profil, on charge le profil par son identifiant
                if (this.profilBackup.Id) {
                    profil = this.profils.find(p => p.Id == this.profilBackup.Id);
                }
                // S'il s'agit d'un nouveau profil, alors on recherche le profil correspondant par son nom (et on prend le dernier trouvé au cas où il y est un profil avec le même nom)
                else {
                    // .slice().reverse().find() est l'équivalent de findLast (non disponible dans cette version)
                    profil = this.profils.slice().reverse().find(p => p.Name === this.profilBackup.Name);
                }

                // Si un profil a été trouvé, alors on charge ses droits
                if (profil) {
                    this.loadProfilValues(profil);
                }

                this.$store.dispatch("HideLoadingMask", {});
            }
        }

        /** Méthode pour modifier les droits */
        private async checkSelection(checked, Nom: string, Type: string) {
            if(checked) {
                let right = this.rights.find(r => r.FunctionName == Nom);

                if (!right) return;

                this.actions.filter(t => t !== Type).forEach(t => right[t] = false);
            }
        }

        /** Computed pour savoir si il y a un profil sélectionné ou non */
        get IsProfilSelected(): boolean {
            return this.profil.Name != '' && this.profil.Id != '';
        }

        /** Liste filtrée basée sur la saisie utilisateur */
        get filteredProfils(): ProfilDto[] {
            const searchLower = this.search.trim().toLowerCase();
            
            // Si la recherche est vide, on retourne la liste complète
            if (!searchLower) {
                return this.profils;
            }

            // Sinon, on filtre
            return this.profils.filter(p => 
                p.Name && p.Name.toLowerCase().includes(searchLower)
            );
        }

        /** Méthode qui retourne l'enum */
        private getBadgeType(headerName: string): BadgeTypeEnum {
            const group = this.groupedRights.find(g => g.Nom === headerName);
            if (!group) return BadgeTypeEnum.None;

            const rules = [
                { type: BadgeTypeEnum.Full, check: () => group.Rights.every(r => r.Delete) },
                { type: BadgeTypeEnum.Add, check: () => group.Rights.every(r => r.Add) },
                { type: BadgeTypeEnum.Read, check: () => group.Rights.every(r => r.Read) },
                { type: BadgeTypeEnum.Write, check: () => group.Rights.every(r => r.Write) },
                { type: BadgeTypeEnum.Personalized, check: () => group.Rights.some(r => r.Add || r.Delete || r.Read || r.Write) }
            ];

            const matchedRule = rules.find(rule => rule.check());
            return matchedRule ? matchedRule.type : BadgeTypeEnum.None;
        }

        /** Méthode qui retourne le text du type de badge */
        private getBadgeText(headerName: string): string {
            const type = this.getBadgeType(headerName);
            return this.$t(type).toString();
        }

        /** Méthode qui retourne la couleur du type de badge */
        private getBadgeVariant(headerName: string): string {
            const type = this.getBadgeType(headerName);
            return this.badgeVariants[type] || 'secondary';
        }

        /** Méthode pour créé tous les groupes et les droits correspondant */
        private buildGroupedRights() {
            const openStateByNom = new Map<string, boolean>(
                this.groupedRights.map(g => [g.Nom, g.IsOpen])
            );

            const groupes: Group[] = [];
            let autoId = 1;

            this.rights.forEach(right => {
                let groupe = groupes.find(g => g.Nom === right.Header);

                if (!groupe) {
                    groupe = {
                        Id: autoId++,
                        Nom: right.Header,
                        Rights: [],
                        IsOpen: openStateByNom.get(right.Header) ?? false,
                    };
                    groupes.push(groupe);
                }

                right.Read = right.Write = right.Add = right.Delete = false;

                if (this.IsProfilSelected) {
                    const profilRight = this.profil.Rights.find(
                        p => p.Function === right.FunctionName
                    );

                    if (profilRight) {
                        right.Read = profilRight.Read;
                        right.Write = profilRight.Write;
                        right.Add = profilRight.Add;
                        right.Delete = profilRight.Delete;
                    }
                }

                groupe.Rights.push(right);
            });

            this.groupedRights = groupes.sort((a, b) =>
                a.Nom.localeCompare(b.Nom)
            );
        }

        /** Méthode pour annuler une modification en cours */
        private cancelModification() {
            let message = [];
            /** forge du message de validation sur le modèle de celui de Sandrine */
            message.push(this.$createElement('div', [
                this.$createElement('b-icon', {props: {icon: 'exclamation-triangle-fill', variant: "warning"}}),
                "  " + this.$t('MsgConfirmInfoDelete').toString()
            ]));

            this.$bvModal.msgBoxConfirm([this.$createElement('p', message)], {
                title: this.$t('CancelProfil').toString(),
                okVariant: 'danger',
                cancelVariant: 'light',
                okTitle: this.$t('Cancel').toString(),
                cancelTitle: this.$t('No').toString(),
                hideHeaderClose: false,
                centered: true
            }).then(async value => {
                // Si l'utilisateur à annulé l'annulation, on sort
                if(!value) {
                    return;
                }

                if (this.isNewProfil) {
                    await this.getProfils();
                    this.isNewProfil = false;
                    this.profilBackup = { ...this.profil };
                    this.rightsBackup = JSON.parse(JSON.stringify(this.rights));

                    if (this.profils[0]) {
                        this.loadProfilValues(this.profils[0]);
                    }
                }
                else {
                    this.profils.find(f => f.Id == this.profilBackup.Id).Name = this.profilBackup.Name;
                    this.loadProfilValues(this.profils.find(f => f.Id == this.profilBackup.Id));
                }

                this.$store.dispatch("HideLoadingMask", {});

                // await this.getProfils();
            });
        }

        /** Méthode pour éviter de parser l'ensemble des droits => sert à la gestion de "Annuler" */
        private rightSignature(list: Right[]): string {
            return list
                .map(r => `${r.FunctionName}:${+!!r.Read}${+!!r.Write}${+!!r.Add}${+!!r.Delete}`)
                .sort()
                .join('|');
        }

        /** Méthode pour connaitre l'état du bouton "Annuler" en fonction du contenu du profil en cours */
        get isSaveDisabled(): boolean {
            if (!this.profil || !this.profilBackup) return true;

            const sameName = this.profil.Name === this.profilBackup.Name;
            const sameRights = this.rightSignature(this.rights) === this.rightSignature(this.rightsBackup);

            return sameName && sameRights;
        }

        /** Appel des ressources nécessaires pour la page */
        async mounted() {
            await this.$store.dispatch("ShowLoadingMask");
            try {
                await Promise.all([this.getProfils(), this.getFunctions()]);
            } finally {
                this.$store.dispatch("HideLoadingMask", {});
            }

            console.debug("Action :", this.Action);
        }

    }

    interface ProfilDto {
        Id: string;
        Name: string;
        Rights: Right[];
    }

    interface Right {
        Header: string;
        HeaderName: string;
        Function: string;
        FunctionName: string;
        Read: boolean;
        Write: boolean;
        Add: boolean;
        Delete: boolean;
    }

    interface Group {
        Id: number,
        Nom: string,
        Rights: Right[],
        IsOpen: boolean,
    }

    /**
    * Types badge possible à avoir sur le Front * 
    *******************************/
    enum BadgeTypeEnum {
        None = "NoAccess",
        Personalized = "PersonalizedAccess",
        Full = "FullAccess",
        Read = "ReadOnly",
        Write = "WriteOnly",
        Add = "AddOnly",
    }
</script>
<style scoped>

    :deep(.custom-control-input:checked ~ .custom-control-label::before) {
        background-color: var(--Variant) !important;
        border-color: var(--Variant) !important;
    }

</style>